import React, { useState, useEffect, useCallback } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Divider, 
  CardMedia, 
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getUrl } from '../utils/app';

const CardGrid = ({ cards, theme }) => {
  const [selectedCard, setSelectedCard] = useState(null);

  const handleCardClick = (card) => {
    setSelectedCard(card);
  };

  const handleClose = () => {
    setSelectedCard(null);
  };

  const getPreviousCard = useCallback((card) => {
    const currentIndex = cards.findIndex((c) => c.password === card.password);
    return currentIndex > 0 ? cards[currentIndex - 1] : cards[cards.length - 1];
  }, [cards]);

  const getNextCard = useCallback((card) => {
    const currentIndex = cards.findIndex((c) => c.password === card.password);
    return currentIndex < cards.length - 1 ? cards[currentIndex + 1] : cards[0];
  }, [cards]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        handleCardClick(getPreviousCard(selectedCard));
      } else if (event.key === 'ArrowRight') {
        handleCardClick(getNextCard(selectedCard));
      }
    };

    if (selectedCard) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedCard, getNextCard, getPreviousCard]);

  if (!cards || cards.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6" color="text.secondary">
          No cards found
        </Typography>
      </Box>
    );
  }

  // Group cards by section
  const cardsBySection = cards.reduce((acc, card) => {
    const section = card.section || '#';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(card);
    return acc;
  }, {});

  // Sort cards within each section
  Object.keys(cardsBySection).forEach(section => {
    cardsBySection[section].sort((a, b) => a.name.localeCompare(b.name));
  });

  return (
    <>
      <Box>
        {Object.entries(cardsBySection).map(([section, sectionCards]) => (
          <Box key={section} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" component="h2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                {section}
              </Typography>
              <Divider sx={{ flex: 1, ml: 2 }} />
            </Box>
            <Grid container spacing={2}>
              {sectionCards.map((card) => (
                <Grid item xs={12} sm={4} md={3} lg={2} key={card.password === 'None' ? card.image : card.password || Math.random()}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                      },
                      cursor: 'pointer',
                      mb: { xs: 2, sm: 0 }
                    }}
                    onClick={() => handleCardClick(card)}
                  >
                    <CardMedia
                      component="img"
                      sx={{
                        aspectRatio: '3/4',
                        objectFit: 'contain',
                        backgroundColor: 'background.default',
                        p: 0.5,
                        maxHeight: { xs: '300px', sm: 'none' }
                      }}
                      image={getUrl(`/images/${card.image}`)}
                      alt={card.name}
                      onError={(e) => {
                        e.target.src = getUrl('/placeholder.svg');
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography 
                        variant="body2" 
                        component="div" 
                        sx={{ 
                          color: 'primary.main', 
                          fontWeight: 'medium',
                          fontSize: { xs: '1rem', sm: '0.875rem' },
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        {card.name} 
                        {card.restriction === 2 && (
                          <span style={{ 
                            backgroundColor: 'orange', 
                            marginLeft: 'auto', 
                            display: 'inline-block', 
                            width: '10px', 
                            height: '10px'
                          }} />
                        )}
                        {card.restriction === 1 && (
                          <span style={{ 
                            backgroundColor: 'red', 
                            marginLeft: 'auto', 
                            display: 'inline-block', 
                            width: '10px', 
                            height: '10px'
                          }} />
                        )}
                      </Typography>
                      {(card.atk || card.def) && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mt: 0.5,
                            fontSize: { xs: '0.875rem', sm: '0.75rem' },
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}
                        >
                          {card.atk && <span>ATK: {card.atk}</span>}
                          {card.def && <span>DEF: {card.def}</span>}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>

      {/* Card Details Modal */}
      <Dialog
        open={Boolean(selectedCard)}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        {selectedCard && (
          <>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" component="div" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                {selectedCard.name}
              </Typography>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{ color: 'grey.500' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <CardMedia
                    component="img"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      aspectRatio: '3/4',
                      objectFit: 'contain',
                      backgroundColor: 'background.default',
                      borderRadius: 1
                    }}
                    image={getUrl(`/images/${selectedCard.image}`)}
                    alt={selectedCard.name}
                    onError={(e) => {
                      e.target.src = getUrl('/placeholder.svg');
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Card Information
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Type:</strong> {selectedCard.cardType}
                    </Typography>
                    {selectedCard.level && (
                      <Typography variant="body1" paragraph>
                        <strong>Level:</strong> {selectedCard.level}
                      </Typography>
                    )}
                    {selectedCard.type && selectedCard.attribute && (
                      <Typography variant="body1" paragraph>
                        <strong>Monster Type:</strong> {selectedCard.type} / {selectedCard.attribute}
                      </Typography>
                    )}
                    {selectedCard.atk && selectedCard.def && (
                      <Typography variant="body1" paragraph>
                        <strong>ATK/DEF:</strong> {selectedCard.atk} / {selectedCard.def}
                      </Typography>
                    )}
                    {selectedCard.restriction && (selectedCard.restriction === 1 || selectedCard.restriction === 2) && (
                      <Typography variant="body1" paragraph sx={{ color: selectedCard.restriction === 1 ? 'error.main' : 'orange' }}>
                        <strong>Restriction:</strong> {selectedCard.restriction === 1 ? 'Limited to 1 card in a deck' : 'Limited to 2 cards in a deck'}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Description
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {selectedCard.description}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary">
                      Additional Details
                    </Typography>
                    <Typography variant="body1" paragraph>
                      <strong>Password:</strong> {selectedCard.password}
                    </Typography>
                    {selectedCard.booster && (
                      <Typography variant="body1" paragraph>
                        <strong>Booster Pack:</strong> {selectedCard.booster}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <IconButton onClick={() => handleCardClick(getPreviousCard(selectedCard))} disabled={!getPreviousCard(selectedCard)}>
                  <ArrowBackIcon />
                </IconButton>
                <IconButton onClick={() => handleCardClick(getNextCard(selectedCard))} disabled={!getNextCard(selectedCard)}>
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default CardGrid;
